/**
 * Carbon Structuring Workspace — /housing/carbon-structuring
 * Uses useHousingJourney() for single source of truth.
 * Evidence checklist reads live from evidence vault — no hardcoded done flags.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, ArrowLeft, CheckCircle2, Download, FileText, ChevronRight, Layers, BarChart2, BookOpen, Shield, Layers as ProgIcon, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { useHousingJourney } from "@/hooks/useHousingJourney";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
// ── CRITICAL_PARAMETERS — mirrors backend housing_mrv.py ──────────────────
const EVIDENCE_PARAMS = [
  { param: "cement_kg",            label: "Cement supply invoice",         desc: "Supplier invoice or BOQ" },
  { param: "scm_substitution_pct", label: "SCM supplier certificate",      desc: "SCM fraction declaration" },
  { param: "steel_kg",             label: "Steel procurement record",       desc: "Structural schedule" },
  { param: "steel_recycled_pct",   label: "Steel mill certificate",        desc: "EAF content declaration" },
  { param: "annual_energy_kwh",    label: "Utility bill",                  desc: "Annual kWh consumption" },
  { param: "solar_fraction_pct",   label: "Solar commissioning report",    desc: "Inverter monitoring data" },
  { param: "capex_usd",            label: "Procurement invoice",           desc: "Low-carbon material costs" },
];

const STEPS = [
  { id: 1, key: "definition",   label: "Project Definition",    icon: Layers,    desc: "From project profile"  },
  { id: 2, key: "baseline",     label: "Baseline & Emissions",  icon: BarChart2, desc: "From climate engine"   },
  { id: 3, key: "methodology",  label: "Methodology",           icon: BookOpen,  desc: "Auto-matched"          },
  { id: 4, key: "mrv",          label: "MRV System",            icon: Shield,    desc: "From data sources"     },
  { id: 5, key: "programme",    label: "Programme Structure",    icon: ProgIcon,  desc: "PoA configuration"     },
  { id: 6, key: "documents",    label: "Document Generation",   icon: FolderOpen,desc: "Download all outputs"  },
];

function StepContent({ stepKey, journey, evidenceCoverage }) {
  const org   = journey?.organization || {};
  const proj  = journey?.project      || {};
  const demo  = journey?.demo_unit    || {};
  const prog  = journey?.programme    || {};
  const mats  = journey?.material_breakdown || [];
  const fr    = journey?.financing_readiness || {};

  const Field = ({ label, value, note }) => (
    <div className="flex items-start justify-between py-2 border-b border-slate-50 last:border-0">
      <div>
        <p className="text-xs font-semibold text-slate-600">{label}</p>
        {note && <p className="text-xs text-slate-400 italic">{note}</p>}
      </div>
      <p className="text-xs font-bold text-slate-900 text-right max-w-[55%] leading-tight">{value}</p>
    </div>
  );

  const Section = ({ title, children }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">{title}</p>
      {children}
    </div>
  );

  if (stepKey === "definition") return (
    <div>
      <Section title="Organisation">
        <Field label="Developer" value={org.name || "Coop Green Homes Ltd"} />
        <Field label="Sector"    value={org.sector || "Real Estate / Green Housing"} />
        <Field label="Country"   value={org.country || "Nigeria"} />
        <Field label="Staff"     value={org.employee_count ? `${org.employee_count} people` : "—"} />
      </Section>
      <Section title="Project">
        <Field label="Project Name"   value={proj.name} />
        <Field label="Type"           value={proj.type} />
        <Field label="Methodology"    value={proj.methodology} />
        <Field label="Demo Unit"      value={proj.demo_unit} />
        <Field label="Programme"      value={proj.programme} />
        <Field label="Status"         value={journey?.status || "Assessed"} />
      </Section>
    </div>
  );

  if (stepKey === "baseline") return (
    <div>
      <Section title="Embodied Carbon (Construction Phase)">
        <Field label="Baseline embodied"  value={`${demo.baseline_embodied_tco2e} tCO2e/unit`} note="Conventional construction reference" />
        <Field label="Project embodied"   value={`${demo.project_embodied_tco2e} tCO2e/unit`}  note="With low-carbon interventions" />
        <Field label="Embodied reduction" value={`${demo.embodied_reduction_tco2e} tCO2e/unit`} note="One-time, at construction" />
      </Section>
      <Section title="Material Breakdown (Baseline)">
        {mats.map(m => <Field key={m.category} label={m.material} value={`${m.baseline_tco2e}t  (${m.baseline_pct}%)`} />)}
      </Section>
      <Section title="Operational Carbon (Annual)">
        <Field label="Baseline operational"  value={`${demo.baseline_operational_tco2e_yr} tCO2e/yr`} />
        <Field label="Project operational"   value={`${demo.project_operational_tco2e_yr} tCO2e/yr`} />
        <Field label="Annual reduction"      value={`${demo.operational_reduction_tco2e_yr} tCO2e/yr`} note="Recurring benefit" />
      </Section>
    </div>
  );

  if (stepKey === "methodology") return (
    <div>
      <Section title="Calculation Methodology">
        <Field label="Engine"         value="DeCarbonIQ DPCS-H v1.0" />
        <Field label="Standard"       value="GHG Protocol Corporate Standard" />
        <Field label="Factor Library" value="DCI-Africa-V1.0" />
        <Field label="Grid Factor"    value="0.43 kgCO2e/kWh (Nigeria, IEA 2023)" />
      </Section>
      <Section title="Emission Factors">
        <Field label="Cement (OPC)"    value="0.875 kgCO2e/kg (IPCC 2006)" />
        <Field label="Cement (40% SCM)" value="0.700 kgCO2e/kg (blended)" />
        <Field label="Steel (virgin)"  value="1.850 kgCO2e/kg (BOF, WSA 2022)" />
        <Field label="Steel (EAF)"     value="0.350 kgCO2e/kg (recycled)" />
        <Field label="Masonry"         value="0.120 kgCO2e/kg (IEA 2023)" />
      </Section>
      <Section title="Methodology Fit">
        <Field label="Primary pathway" value="DPCS-H v1.0 (Embodied + Operational)" />
        <Field label="VCM match"       value="Indicative — AMS-III.AV / Verra LCCA" note="Third-party validation required" />
        <Field label="PoA match"       value="Standardized design qualifies" />
      </Section>
    </div>
  );

  if (stepKey === "mrv") return (
    <div>
      <Section title="Current Data Status">
        <Field label="Confidence"    value="Medium" note="Self-reported inputs" />
        <Field label="Data sources"  value="BOQ, supplier specifications, utility estimates" />
        <Field label="Monitoring"    value="Quarterly (construction), Annual (operational)" />
      </Section>
      <Section title="Evidence Requirements (Live Status)">
        {EVIDENCE_PARAMS.map(({ param, label, desc }) => {
          const done = evidenceCoverage?.isVerified(param);
          const uploaded = evidenceCoverage?.has(param);
          return (
            <div key={param} className="flex items-start gap-2.5 py-2 border-b border-slate-50 last:border-0">
              {done
                ? <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                : uploaded
                ? <div className="w-3.5 h-3.5 rounded-full border-2 border-amber-400 flex-shrink-0 mt-0.5" />
                : <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 flex-shrink-0 mt-0.5" />}
              <div>
                <p className="text-xs font-semibold text-slate-700">{label}</p>
                <p className="text-xs text-slate-400">{desc}</p>
                {done && <p className="text-xs text-emerald-600 font-semibold">Verified</p>}
                {!done && uploaded && <p className="text-xs text-amber-600 font-semibold">Uploaded — under review</p>}
                {!uploaded && <p className="text-xs text-slate-400 italic">Not yet uploaded</p>}
              </div>
            </div>
          );
        })}
        <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
          <span className="text-slate-400">Coverage: {evidenceCoverage?.critical_covered || 0}/{evidenceCoverage?.critical_total || 7} verified</span>
          <span className={`font-bold ${(evidenceCoverage?.critical_covered || 0) >= 7 ? "text-emerald-600" : "text-amber-600"}`}>
            {(evidenceCoverage?.coverage_pct || 0)}% complete
          </span>
        </div>
      </Section>
      <Section title="Verification Plan">
        <Field label="Verification body"    value="Accredited third-party (Gold Standard / Verra)" />
        <Field label="Trigger"              value="After construction completion; annual operational" />
        <Field label="Sample size"          value="100% demo unit; 10% programme minimum" />
      </Section>
    </div>
  );

  if (stepKey === "programme") return (
    <div>
      <Section title="PoA Configuration (Auto-Generated)">
        <Field label="Programme type"       value="Programme of Activities (PoA)" />
        <Field label="Programme scale"      value={`${prog.units_count?.toLocaleString()} units`} />
        <Field label="Standardized design"  value="Yes — identical BOQ and specifications" />
        <Field label="Is repeatable"        value="Yes — qualifies for PoA structure" />
        <Field label="Capex per unit"       value={`$${demo.capex_usd?.toLocaleString()}`} />
        <Field label="Total programme capex" value={`$${prog.capex_total_usd?.toLocaleString()}`} />
      </Section>
      <Section title="Programme Economics">
        <Field label="Total embodied red." value={`${prog.total_embodied_tco2e?.toLocaleString()} tCO2e`} note="Construction phase" />
        <Field label="Annual operational"  value={`${prog.annual_operational_tco2e?.toLocaleString()} tCO2e/yr`} />
        <Field label="3yr indicative"      value={`~${prog.cumulative_3yr_tco2e?.toLocaleString()} tCO2e`} />
        <Field label="Energy savings"      value={`$${prog.energy_savings_total_usd_yr?.toLocaleString()}/yr`} />
      </Section>
      <Section title="Programme Coordinator">
        <Field label="Proposed PCE"  value={org.name || "Coop Green Homes Ltd"} note="Programme Coordinating Entity" />
        <Field label="Role"          value="Programme design, MRV coordination, buyer engagement" />
        <Field label="Legal basis"   value="To be established — requires legal ownership declaration" />
      </Section>
    </div>
  );

  if (stepKey === "documents") return null; // handled separately

  return null;
}

function DownloadButton({ label, subtitle, endpoint, filename, type = "primary" }) {
  const [loading, setLoading] = useState(false);
  const download = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("decarboniq_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`${label} downloaded.`);
    } catch { toast.error("Download failed."); }
    setLoading(false);
  };
  return (
    <button onClick={download} disabled={loading}
      className={`w-full flex items-start gap-3 px-5 py-4 rounded-xl border-2 text-left transition-colors disabled:opacity-50 ${
        type === "primary"
          ? "border-teal-200 bg-teal-50 hover:bg-teal-100"
          : type === "secondary"
          ? "border-slate-200 bg-white hover:bg-slate-50"
          : "border-amber-200 bg-amber-50 hover:bg-amber-100"
      }`}>
      {loading ? <RefreshCw size={16} className="text-teal-500 animate-spin flex-shrink-0 mt-0.5" /> : <Download size={16} className="text-slate-600 flex-shrink-0 mt-0.5" />}
      <div className="flex-1">
        <p className="text-sm font-bold text-slate-900">{label}</p>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </div>
    </button>
  );
}

export default function CarbonStructuringWorkspace() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const { journey, loading, org, evidenceCoverage } = useHousingJourney();
  if (loading) return <div className="flex items-center justify-center min-h-[40vh]"><RefreshCw size={18} className="animate-spin text-teal-500" /></div>;
  const currentStep = STEPS.find(s => s.id === step) || STEPS[0];

  return (
    <div className="p-6 lg:p-8 min-h-screen">

        {/* Back + header — Fix 4: Clear purpose statement */}
        <button onClick={() => navigate("/housing/carbon-strategy")} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-5">
          <ArrowLeft size={13} /> Carbon Strategy
        </button>
        <div className="mb-6">
          
      <PageHeader
        title="Carbon Structuring"
        backUrl="/housing/carbon-strategy"
        backLabel="Carbon Strategy"
      />
          <p className="text-xs text-slate-500 mt-0.5 max-w-xl leading-relaxed">
            Prepare this project for carbon market participation — methodology alignment, MRV setup, programme design, and document generation.
          </p>
        </div>

        {/* Fix 4: Expected outputs clearly visible */}
        <SectionCard>

          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Expected Outputs from This Workflow</p>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
            {[
              { label: "Draft PDD", status: "available",    color: "border-teal-200 bg-teal-50 text-teal-700" },
              { label: "MRV Plan",  status: "available",    color: "border-teal-200 bg-teal-50 text-teal-700" },
              { label: "Emission Calculations", status: "available", color: "border-teal-200 bg-teal-50 text-teal-700" },
              { label: "Carbon Project Pack",   status: "available", color: "border-blue-200 bg-blue-50 text-blue-700" },
              { label: "Article 6 Pack",        status: "template",  color: "border-amber-200 bg-amber-50 text-amber-700" },
            ].map(o => (
              <div key={o.label} className={`rounded-xl border px-3 py-2 text-center ${o.color}`}>
                <p className="text-xs font-bold">{o.label}</p>
                <p className="text-xs mt-0.5 opacity-70 capitalize">{o.status}</p>
              </div>
            ))}
          </div>
        
        </SectionCard>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">

          {/* Left: Step rail (1/4) */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden sticky top-4">
              {STEPS.map((s, i) => {
                const isActive   = step === s.id;
                const isComplete = step > s.id;
                const Icon = s.icon;
                return (
                  <button key={s.id} onClick={() => setStep(s.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-slate-50 last:border-0 transition-colors ${
                      isActive ? "bg-[#0F172A]" : "hover:bg-slate-50"
                    }`}
                    data-testid={`step-${s.key}`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                      isActive   ? "bg-teal-500 text-white" :
                      isComplete ? "bg-emerald-500 text-white" :
                                   "bg-slate-100 text-slate-500"
                    }`}>
                      {isComplete ? <CheckCircle2 size={13} /> : s.id}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-bold truncate ${isActive ? "text-white" : "text-slate-900"}`}>{s.label}</p>
                      <p className={`text-xs truncate ${isActive ? "text-slate-400" : "text-slate-400"}`}>{s.desc}</p>
                    </div>
                    {isActive && <ChevronRight size={12} className="text-slate-400 flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: Content (3/4) */}
          <div className="lg:col-span-3">
            {step < 6 ? (
              <div>
                {/* Step header */}
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-0.5">Step {step} of 6</p>
                    <h2 className="text-lg font-bold text-slate-900">{currentStep.label}</h2>
                    <p className="text-xs text-slate-400">All fields prefilled from project data</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button disabled={step <= 1} onClick={() => setStep(s => s - 1)}
                      className="px-4 py-2 border border-slate-200 text-xs text-slate-600 rounded-xl hover:bg-slate-50 disabled:opacity-40">
                      Previous
                    </button>
                    <button onClick={() => setStep(s => Math.min(s + 1, 6))}
                      className="px-4 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 flex items-center gap-1.5">
                      {step === 5 ? "Generate Documents" : "Next"} <ChevronRight size={12} />
                    </button>
                  </div>
                </div>
                <StepContent stepKey={currentStep.key} journey={journey} evidenceCoverage={evidenceCoverage} />
              </div>
            ) : (
              /* Step 6: Document generation */
              <div>
                <div className="mb-5">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-0.5">Step 6 of 6</p>
                  <h2 className="text-lg font-bold text-slate-900">Document Generation</h2>
                  <p className="text-xs text-slate-400">All documents auto-generated from project data</p>
                </div>

                <div className="space-y-5">
                  {/* Core documents */}
                  <SectionCard>

                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Core Reports</p>
                    <div className="space-y-2.5">
                      <DownloadButton label="Climate Credentials Report" subtitle="Technical audit document with QR verification" endpoint="/api/housing/credentials-report" filename="CoopGreenHomes-ClimateCredentials.pdf" type="primary" />
                      <DownloadButton label="Finance Brief" subtitle="Financial-institution-grade decision document" endpoint="/api/housing/finance-brief-v2" filename="CoopGreenHomes-FinanceBrief-v2.pdf" type="secondary" />
                    </div>
                            </SectionCard>

                  {/* Carbon project pack */}
                  <div className="bg-white border border-slate-200 rounded-xl p-5">
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Carbon Project Pack</p>
                    <div className="space-y-2.5">
                      <DownloadButton label="Project Design Document (PDD)" subtitle="Full project description — DPCS-H v1.0" endpoint="/api/housing/pdd" filename="CoopGreenHomes-PDD.pdf" type="primary" />
                      <DownloadButton label="MRV Plan" subtitle="Monitoring, reporting and verification methodology" endpoint="/api/housing/mrv-plan" filename="CoopGreenHomes-MRVPlan.pdf" type="secondary" />
                      <DownloadButton label="Emission Calculations" subtitle="Full factor audit trail and calculation detail" endpoint="/api/housing/emission-calculations" filename="CoopGreenHomes-EmissionCalc.pdf" type="secondary" />
                    </div>
                  </div>

                  {/* Article 6 pack */}
                  <div className="bg-white border-2 border-amber-200 rounded-xl p-5">
                    <div className="flex items-start justify-between mb-1">
                      <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Article 6 Readiness Pack</p>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300">Not Eligible — Template</span>
                    </div>
                    <p className="text-xs text-slate-500 italic mb-3">
                      7 documents showing the required structure for host country authorization. Readiness template only — not a formal submission.
                    </p>
                    <DownloadButton
                      label="Article 6 Readiness Pack (7 documents)"
                      subtitle="Authorization request, host country alignment, corresponding adjustment, SD report, MRV plan, legal declaration, PDD"
                      endpoint="/api/housing/article6-pack"
                      filename="CoopGreenHomes-Article6ReadinessPack.pdf"
                      type="warning"
                    />
                  </div>

                </div>

                <button onClick={() => navigate("/housing/reports")}
                  className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 border border-slate-200 text-xs text-slate-600 rounded-xl hover:bg-slate-50">
                  View all reports in Reports section <ChevronRight size={12} />
                </button>
              </div>
            )}
          </div>
        </div>
    </div>
  );
}
